import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ColDef, ProcessCellForExportParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { FC, useCallback, useMemo } from 'react';
import { AgTable } from '../../../../../components/AgTable/AgTable';
import { commonProcessCb } from '../../../../../components/AgTable/exportToExcelDefs';
import { FadeInGridWrapper } from '../../../../../components/grid-renderers/FadeInGridWrapper';
import { roundsByIdMapAtom } from '../../../../../services/state/AppConfigStateJ';
import { IDealStageDataModel } from '../../../data-models/dealStage.data-model';
import { IDealStatsDataModel } from '../../../../../data-models/deal-stats.data-model';
import { LoadingStatus } from '../../../../../types';
import { dealStagesByIdMapState, dealTypesByIdMapState } from '../../../state/DealboardDataState';
import { dealsStatsHistory, IDealConversionStateKey } from '../../../state/DealConversionState';
import {
  dealStatsToRowData,
  getColDefs,
  getDefaultColDef,
  IDealAnalyticsGriData,
} from './DealAnalyticsGridUtils';
import { useFiltersParams } from './hooks/useFiltersParams';

const DealAnalyticsWrapper = styled('div')`
  display: grid;
  grid-template-rows: min-content 1fr;
  height: 100%;
  row-gap: 1rem;
`;

const InfoMessage = styled('div')`
  display: grid;
  grid-template-columns: min-content 1fr;
  column-gap: 0.25rem;
  width: 100%;
`;

export const DealAnalyticsDuration: FC = () => {
  const { queryParams, selectedDealType } = useFiltersParams();
  const { data, loading } = useAtomValue(dealsStatsHistory(queryParams as IDealConversionStateKey));

  return (
    <DealAnalyticsDurationView
      stats={data}
      loading={loading}
      selectedDealType={selectedDealType}
      gridKey={`${queryParams.from}-${selectedDealType}`}
    />
  );
};

interface DealAnalyticsDurationViewProps {
  stats: IDealStatsDataModel[] | null;
  loading: LoadingStatus;
  selectedDealType: number;
  /** Remounts the grid when the filters change. */
  gridKey: string;
}

/** The grid itself. Split out so the frosted sample can render it over fictional stats. */
export const DealAnalyticsDurationView: FC<DealAnalyticsDurationViewProps> = ({
  stats: data,
  loading: dealStatsStatus,
  selectedDealType,
  gridKey,
}) => {
  const defaultColDef: ColDef = getDefaultColDef();
  const dealTypesById = useAtomValue(dealTypesByIdMapState);
  const dealStagesById = useAtomValue(dealStagesByIdMapState);
  const roundsById = useAtomValue(roundsByIdMapAtom);

  const columnDefs: ColDef[] = useMemo(() => {
    const stageIdsForType = dealTypesById?.get(selectedDealType)?.dealStageIds ?? [];
    const stagesForType = stageIdsForType
      .map((stageId) => dealStagesById?.get(stageId) ?? null)
      .filter((stage) => stage !== null) as IDealStageDataModel[];
    return getColDefs(stagesForType);
  }, [dealStagesById, dealTypesById, selectedDealType]);

  const rowData = useMemo(() => {
    if (dealStatsStatus === 'success') {
      return dealStatsToRowData(data ?? [], roundsById);
    }
    return [];
  }, [data, dealStatsStatus, roundsById]);

  const processCellCallback = useCallback(
    (params: ProcessCellForExportParams<IDealAnalyticsGriData>) => {
      if (params.column.getColId() === 'company') {
        return params.node?.data?.company?.name ?? '';
      }
      if (params.column.getColId() === 'round') {
        const roundId = params.node?.data?.roundId;
        if (typeof roundId !== 'number') {
          return '';
        }
        return roundsById.get(roundId)?.displayName ?? '';
      }
      return commonProcessCb(params);
    },
    [roundsById]
  );

  return (
    <DealAnalyticsWrapper>
      <InfoMessage>
        <InfoOutlinedIcon fontSize={'small'} />
        <Typography variant={'body2'}>All duration are displayed in days</Typography>
      </InfoMessage>
      <FadeInGridWrapper key={gridKey}>
        <AgTable
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          defaultCsvExportParams={{ processCellCallback }}
          loading={dealStatsStatus === 'loading'}
          rowData={rowData}
          rowGroupPanelShow='never'
        />
      </FadeInGridWrapper>
    </DealAnalyticsWrapper>
  );
};
